import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Deck framing, 14 x 16 ft (224 sq ft), pressure-treated joists 16 in on center, 6 concrete footings', amount: '$5,400' },
  { item: 'Composite decking, grooved boards with hidden fasteners, picture-frame border', amount: '$4,950' },
  { item: 'Railing, 38 linear ft of composite rail with aluminum balusters; one 4 ft stair with 4 risers', amount: '$3,150' },
  { item: 'Permit drawings and permit submission; inspections scheduled with the town', amount: '$450' },
  { item: 'Optional: low-voltage post-cap and stair-riser lights', amount: '$680' },
];

const faq = [
  {
    q: 'How do deck builders price a new deck?',
    a: 'Usually by area for framing and decking, with railings by the linear foot, stairs by the step or riser, and footings, permits and demolition as their own lines. The decking material is often the biggest swing, so many builders quote the same deck in two materials.',
  },
  {
    q: 'Can I quote pressure-treated and composite on the same estimate?',
    a: 'Put the base deck in one material and the upgrade as an optional line showing the difference, or send the homeowner two quotes for the same request if you want two complete options. In Scopie the homeowner ticks an optional upgrade on the emailed quote and sees the new total before signing.',
  },
  {
    q: 'Who should handle the permit?',
    a: 'Many deck builders pull the permit themselves because inspections follow the build schedule. Whatever you do, say it on the estimate and include the cost if you are handling it.',
  },
  {
    q: 'Does Scopie draw decks or produce a materials list?',
    a: 'No. There is no deck design tool or material takeoff in Scopie. You design and price the deck your way, and Scopie handles the estimate, the signature, the schedule and the invoice.',
  },
];

const page: IndustryPage = {
  slug: 'deck-building',
  order: 20,
  group: 'Outdoor',
  path: '/for/deck-building',
  primaryKeyword: 'deck estimate software',
  secondaryKeywords: ['deck building quote template', 'how to price a deck', 'deck contractor software'],
  title: 'Deck Builder Software for Estimates and Upgrades | Scopie', // 56
  metaDescription:
    'Quote decks with framing, decking, railing, stairs, footings and permit on their own lines, offer composite or lighting upgrades, and sign before building.',
  h1: 'Deck estimates with framing, finish and upgrades in plain view',
  answerFirst:
    'Scopie helps deck builders quote a deck in its real parts: footings and framing, the decking boards, railings by the foot, stairs, permit and demolition. Material and lighting upgrades sit on the estimate as options the homeowner can tick, and the deposit rule is signed before you order lumber or composite.',
  exampleQuote: { label: 'Sample new deck estimate', lines, total: '$14,630', depositNote: 'Material deposit and permit terms are the builder\'s own clauses.' },
  blocks: [
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A 14 x 16 composite deck',
      label: 'Sample estimate: framing, composite boards, rail and stair',
      lines,
      total: '$14,630',
      totalLabel: 'Total if the lighting is added',
      note: 'Illustrative figures. The lights are optional; the homeowner can sign for the deck alone.',
    },
    {
      type: 'text',
      id: 'materials',
      h2: 'The material decision drives the price',
      lead: '"What would it cost in the composite my neighbor has?"',
      paragraphs: [
        'Homeowners rarely know whether they want pressure-treated, cedar or composite until they see the difference in money. Showing the frame and structure once, and the decking choice as the part that changes, makes that comparison easy and keeps the conversation on the deck instead of on your price.',
        'Railing style, fascia boards, picture-frame borders and hidden fasteners are the other big levers. Each deserves a line, so the homeowner can see where the money goes and what they are choosing.',
      ],
    },
    {
      type: 'list',
      id: 'site',
      h2: 'Site questions to answer before you price',
      style: 'checklist',
      items: [
        { text: 'Height off grade, which drives footing depth, posts, bracing and whether guardrails are required.' },
        { text: 'Ledger attachment: is there a sound rim joist, and what siding has to come off?' },
        { text: 'Soil and frost depth where you are, which set footing size and depth.' },
        { text: 'Old deck demolition and disposal.' },
        { text: 'Access for materials and a mini excavator or auger.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Where deck estimates lose money',
      style: 'mistakes',
      items: [
        { title: 'Stairs priced as an afterthought', text: 'Stringers, landings and extra railing make stairs slow. Price per riser.' },
        { title: 'No line for the ledger', text: 'Flashing and fastening to the house is critical work. Show it.' },
        { title: 'Permit time ignored', text: 'Drawings, submissions and inspection visits are real hours.' },
        { title: 'Composite ordered before a deposit', text: 'Colors and lengths are hard to return. Put your deposit rule on the estimate.' },
        { title: 'Old deck removal assumed free', text: 'Tear-out and dump fees belong on the estimate.' },
      ],
    },
    {
      type: 'text',
      id: 'season',
      h2: 'Deck season starts in winter',
      paragraphs: [
        'Homeowners plan decks in late winter and want them by summer, which means the best-booked builders sign estimates in February and March. Once signed, the build goes on the schedule with the crew and equipment; crew members see their addresses, not the price. When the final inspection passes, email the invoice link, and keep the homeowner in your client list for the pergola next year.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Deck estimate questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/fencing', anchor: 'Scopie for fence contractors', status: 'live' },
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
  ],
  cta: {
    heading: 'Sign next summer\'s decks this winter',
    text: 'Store your framing, decking and railing items in Scopie, and email each deck estimate with its upgrades.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
